import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../../shared/ui/Modal';
import { Input } from '../../shared/ui/Input';
import { Button } from '../../shared/ui/Button';
import { useRenameSubscription } from './api';
import { SUB_LABEL_MAX } from './labels';

export function RenameSubscriptionModal({
  subscriptionId,
  initialLabel,
  open,
  onOpenChange,
}: {
  subscriptionId: string | null;
  initialLabel: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const rename = useRenameSubscription();
  const [draft, setDraft] = useState(initialLabel.slice(0, SUB_LABEL_MAX));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setDraft(initialLabel.slice(0, SUB_LABEL_MAX));
      setError(null);
    }
  }, [open, initialLabel]);

  const save = (): void => {
    const next = draft.trim();
    if (!next) {
      setError(t('subscriptions.renameRequired'));
      return;
    }
    if (!subscriptionId) return;
    rename.mutate(
      { id: subscriptionId, serviceLabel: next.slice(0, SUB_LABEL_MAX) },
      {
        onSuccess: () => {
          onOpenChange(false);
        },
        onError: () => {
          setError(t('subscriptions.renameFailed'));
        },
      },
    );
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={t('subscriptions.renameTitle')}
      footer={
        <>
          <Button variant="outline" onClick={() => { onOpenChange(false); }}>
            {t('common.cancel')}
          </Button>
          <Button loading={rename.isPending} onClick={save}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <Input
        label={t('subscriptions.renameField')}
        value={draft}
        maxLength={SUB_LABEL_MAX}
        error={error}
        onChange={(e) => {
          setDraft(e.target.value.slice(0, SUB_LABEL_MAX));
          setError(null);
        }}
      />
      <p className="mt-2 text-xs text-fg-muted">{t('subscriptions.renameHint', { max: SUB_LABEL_MAX })}</p>
    </Modal>
  );
}
